import { Game } from '../types/Game'

export interface LocalizedCloneFields {
  name?: string
  desc?: string
}

/**
 * Builds the list of origin values a clone field could have been generated from:
 * the single default-language value plus every entry of the localized map.
 * Sorted by length descending so that the longest (most specific) candidate matches first.
 */
const buildOriginCandidates = (defaultValue: string | undefined, localizedValues: { [key: string]: string } | undefined): string[] => {
  const candidates = [defaultValue, ...Object.values(localizedValues ?? {})].filter((value): value is string => !!value)

  return Array.from(new Set(candidates)).sort((a, b) => b.length - a.length)
}

/**
 * If the clone value starts with one of the origin candidates, replaces that prefix with the
 * user-language resolved value while preserving any trailing text (e.g. " (Clone)" or a
 * "(cloned from ... by ...)" marker) verbatim. Returns undefined when no replacement applies.
 */
const replaceLocalizedPrefix = (cloneValue: string | undefined, candidates: string[], resolvedValue: string | undefined): string | undefined => {
  if (!cloneValue || !resolvedValue) {
    return undefined
  }

  const matchedCandidate = candidates.find((candidate) => cloneValue.startsWith(candidate))

  if (!matchedCandidate || matchedCandidate === resolvedValue) {
    return undefined
  }

  return resolvedValue + cloneValue.slice(matchedCandidate.length)
}

/**
 * Resolves the user-language name and description for a freshly cloned game (GR-1442).
 *
 * The backend builds the clone's single name/desc from the origin's default-language values
 * (e.g. "Brawl Stars (Clone)"), so for ja/zh users they don't match the game view page, which
 * displays resolvedName/resolvedDescription. This helper swaps the default-language base value
 * for the user-language one while preserving any surrounding text it does not recognize, such
 * as the " (Clone)" suffix (GR-1521).
 *
 * Returns only the changed properties, or undefined when neither field would change
 * (e.g. for EN users), so callers can keep the untouched server values as-is.
 */
export const getLocalizedCloneFields = (clone: Pick<Game, 'name' | 'desc'>, origin: Game): LocalizedCloneFields | undefined => {
  const localizedName = replaceLocalizedPrefix(clone.name, buildOriginCandidates(origin.name, origin.names), origin.resolvedName)
  const localizedDesc = replaceLocalizedPrefix(clone.desc, buildOriginCandidates(origin.desc, origin.descriptions), origin.resolvedDescription)

  if (localizedName === undefined && localizedDesc === undefined) {
    return undefined
  }

  return {
    ...(localizedName !== undefined && { name: localizedName }),
    ...(localizedDesc !== undefined && { desc: localizedDesc }),
  }
}
